import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to update HTML lang attribute based on i18n language selection
 * Automatically updates document.documentElement.lang when language changes
 * 
 * This hook ensures that:
 * - Screen readers and assistive technologies know the page language
 * - Search engines understand the content language  
 * - Browsers can provide proper spell checking and translation
 * - HTML document lang attribute is always in sync with user's selected language
 */
export const useDocumentLanguage = () => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Update the document's lang attribute when language changes
    const currentLanguage = i18n.language || 'en';
    
    // Update both html and documentElement lang attributes for better accessibility
    document.documentElement.lang = currentLanguage;
    
    // Also set the lang attribute on the html tag for better browser support
    const htmlElement = document.querySelector('html');
    if (htmlElement) {
      htmlElement.setAttribute('lang', currentLanguage);
    }
    
    // Optional: Log for debugging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log(`🌐 Document language updated to: ${currentLanguage}`);
    }
  }, [i18n.language]);
};

export default useDocumentLanguage;
